import { useState } from "react";
import { Phone, Download, MonitorPlay, KeyRound, CheckCircle2, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { PageHero } from "../components/PageHero";
import { Reveal, Eyebrow } from "../components/Reveal";
import { FaqList } from "../components/FaqList";
import { Field, inputCls, areaCls, ChoiceGroup } from "../components/FormBits";
import { CONTACT, SUPPORT_FAQ } from "../data/content";
import { api, errorMessage } from "../lib/api";

const URGENCY = [
  { value: "bloquant", label: "Bloquant", desc: "Toute l'activité est à l'arrêt", c: "#E5484D" },
  { value: "genant", label: "Gênant", desc: "Une partie de l'équipe est impactée", c: "#EE8445" },
  { value: "normal", label: "Normal", desc: "Ça peut attendre quelques heures", c: "#45ADE0" },
  { value: "question", label: "Question", desc: "Demande d'information ou de conseil", c: "#A6C46B" },
];
const TYPES = ["Poste / ordinateur", "Réseau / Internet", "Wi-Fi", "Téléphonie", "Messagerie / Microsoft 365", "Imprimante", "Autre"];
const ANYDESK = [
  [Download, "Téléchargez", "Cliquez sur le bouton ci-dessous pour récupérer AnyDesk (aucune installation requise)."],
  [MonitorPlay, "Lancez", "Ouvrez le fichier téléchargé. Une fenêtre s'affiche avec « Votre adresse »."],
  [KeyRound, "Communiquez", "Donnez ce numéro à 9 chiffres au technicien ENOVA, par téléphone ou dans le formulaire."],
];
const EMPTY = { name: "", company: "", email: "", phone: "", urgency: "", problem_type: "", description: "", anydesk_id: "" };

const TicketForm = () => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.urgency || !f.problem_type) return toast.error("Indiquez le niveau d'urgence et le type de problème.");
    setBusy(true);
    try {
      const { data } = await api.post("/support", f);
      setDone(data);
      setF(EMPTY);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <div data-testid="support-success" className="bg-white border-2 border-[#A6C46B] p-10">
        <CheckCircle2 className="text-[#A6C46B]" size={40} />
        <h3 className="font-display text-2xl font-bold mt-5">Demande envoyée.</h3>
        <p className="text-ink/65 mt-2">Référence <strong data-testid="support-ticket-ref">#{done.id.slice(-6).toUpperCase()}</strong>. Elle a été transmise au support et un e-mail de confirmation vous a été adressé. Pour une urgence bloquante, appelez directement le support au {CONTACT.hotline} ({CONTACT.hoursShort.toLowerCase()}).</p>
        <button data-testid="support-new-btn" onClick={() => setDone(null)} className="btn-outline mt-6">Nouvelle demande</button>
      </div>
    );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="support-form">
      <Field label="Niveau d'urgence"><ChoiceGroup prefix="support-urgency" options={URGENCY} value={f.urgency} onChange={set("urgency")} /></Field>
      <Field label="Type de problème">
        <select data-testid="support-type-select" className={inputCls} value={f.problem_type} onChange={set("problem_type")} required>
          <option value="">Choisir…</option>
          {TYPES.map((t) => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="Décrivez le problème"><textarea data-testid="support-description-input" className={areaCls} value={f.description} onChange={set("description")} required minLength={5} placeholder="Depuis ce matin, l'imprimante de l'accueil…" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nom"><input data-testid="support-name-input" className={inputCls} value={f.name} onChange={set("name")} required /></Field>
        <Field label="Entreprise"><input data-testid="support-company-input" className={inputCls} value={f.company} onChange={set("company")} /></Field>
        <Field label="E-mail"><input type="email" data-testid="support-email-input" className={inputCls} value={f.email} onChange={set("email")} required /></Field>
        <Field label="Téléphone"><input type="tel" data-testid="support-phone-input" className={inputCls} value={f.phone} onChange={set("phone")} required minLength={6} /></Field>
      </div>
      <Field label="Identifiant AnyDesk (facultatif)"><input data-testid="support-anydesk-input" className={inputCls} value={f.anydesk_id} onChange={set("anydesk_id")} placeholder="123 456 789" /></Field>
      <button disabled={busy} data-testid="support-submit-btn" className="btn-primary w-full sm:w-auto" style={{ background: "#E3B321", color: "#2b2b2b" }}>
        {busy && <Loader2 className="animate-spin" size={18} />} Envoyer la demande d'assistance
      </button>
    </form>
  );
};

export default function Support() {
  return (
    <div data-testid="support-page">
      <PageHero eyebrow="Assistance" color="#E3B321" title="Une panne ? On s'en occupe." lead="Notre service support est ouvert de 7h à 17h : appelez, un technicien prend en charge votre demande immédiatement. Vous pouvez aussi ouvrir une demande en ligne.">
        <div className="mt-10 flex flex-wrap items-center gap-6">
          <a href={CONTACT.hotlineHref} data-testid="support-hotline-btn" className="group inline-flex items-center gap-4 bg-[#E3B321] text-ink px-7 py-5 hover:brightness-105 transition-[filter]">
            <Phone size={28} />
            <span><span className="block text-xs uppercase tracking-[0.18em] font-semibold">Hotline</span><span className="font-display text-2xl sm:text-3xl font-extrabold">{CONTACT.hotline}</span></span>
          </a>
          <div className="text-sm text-white/70 max-w-xs space-y-1">
            <p>{CONTACT.hoursShort}</p>
            <p>Par e-mail : <a href={`mailto:${CONTACT.supportEmail}`} data-testid="support-email-link" className="text-white underline underline-offset-4">{CONTACT.supportEmail}</a></p>
          </div>
        </div>
      </PageHero>
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <Eyebrow color="#E3B321">Demande d'assistance</Eyebrow>
          <h2 className="mt-5 mb-10 font-display text-3xl font-extrabold">Décrivez-nous la situation.</h2>
          <TicketForm />
        </div>
        <aside className="lg:col-span-5">
          <Reveal className="bg-ink text-white p-8 lg:p-10 sticky top-28" >
            <div data-testid="anydesk-block">
              <Eyebrow color="#45ADE0" light>Prise en main à distance</Eyebrow>
              <h3 className="mt-5 font-display text-2xl font-bold">Laissez le technicien voir votre écran.</h3>
              <ol className="mt-8 space-y-6">
                {ANYDESK.map(([Icon, t, d], i) => (
                  <li key={t} className="flex gap-4">
                    <span className="h-10 w-10 shrink-0 grid place-items-center bg-white/10"><Icon size={18} className="text-[#45ADE0]" /></span>
                    <span><span className="font-semibold">{i + 1}. {t}</span><span className="block text-sm text-white/60 mt-1">{d}</span></span>
                  </li>
                ))}
              </ol>
              <a href="https://anydesk.com/fr/downloads" target="_blank" rel="noopener noreferrer" data-testid="anydesk-download-btn" className="btn-primary mt-9 w-full justify-center">
                <Download size={18} /> Télécharger AnyDesk
              </a>
              <p className="text-xs text-white/40 mt-4">N'acceptez une connexion que si vous êtes en ligne avec un technicien ENOVA.</p>
            </div>
            <div data-testid="securepass-block" className="mt-10 pt-8 border-t border-white/10">
              <Eyebrow color="#A6C46B" light>Mot de passe à transmettre ?</Eyebrow>
              <p className="mt-4 text-sm text-white/70">Ne l'envoyez jamais par e-mail ou par SMS. Utilisez SecurePass : un lien chiffré, lisible une seule fois, qui s'autodétruit.</p>
              <a href="https://securepass.pro" target="_blank" rel="noopener noreferrer" data-testid="securepass-link" className="btn-ghost-light mt-5">
                <ShieldCheck size={16} /> Partager via SecurePass
              </a>
            </div>
          </Reveal>
        </aside>
      </section>
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Eyebrow color="#E3B321">Dépannage rapide</Eyebrow>
          <h2 className="mt-5 font-display text-3xl font-extrabold">Avant d'appeler, essayez ceci.</h2>
        </div>
        <div className="lg:col-span-8"><FaqList items={SUPPORT_FAQ} color="#E3B321" prefix="support-faq" /></div>
      </section>
    </div>
  );
}
